"use client";

import React, { useState, useEffect } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Cpu, Bot, CircuitBoard, Cog, Wrench, Sparkles, ArrowRight, Info, Compass, CircleCheck as CheckCircle2, Rocket, ShieldCheck, Layers, Zap, ChevronRight } from 'lucide-react';
interface StageItem {
  id: number;
  code: string;
  badgeLabel: string;
  title: string;
  ageVi: string;
  ageEn: string;
  descVi: string;
  descEn: string;
  icon: React.ElementType;
  isSpecial?: boolean;
}
const STAGES: StageItem[] = [{
  id: 1,
  code: "D1",
  badgeLabel: "STAGE 01",
  title: "Bé làm kỹ sư",
  ageVi: "4 - 5 tuổi",
  ageEn: "Ages 4 - 5",
  descVi: "Làm quen với cấu trúc cơ khí cơ bản, bánh răng, đòn bẩy và khám phá thế giới tự nhiên qua mô hình LEGO.",
  descEn: "Introduction to basic mechanical structures, gears, levers, and exploring the physical world through LEGO models.",
  icon: Wrench
}, {
  id: 2,
  code: "D2",
  badgeLabel: "STAGE 02",
  title: "Thành phố của em",
  ageVi: "5 - 6 tuổi",
  ageEn: "Ages 5 - 6",
  descVi: "Xây dựng các công trình đô thị, phương tiện giao thông và hình thành tư duy không gian, phối hợp nhóm.",
  descEn: "Building urban infrastructure, transportation models, developing spatial thinking and team collaboration.",
  icon: Compass
}, {
  id: 3,
  code: "D3",
  badgeLabel: "STAGE 03",
  title: "Cỗ máy kỳ diệu",
  ageVi: "6 - 7 tuổi",
  ageEn: "Ages 6 - 7",
  descVi: "Khám phá nguyên lý chuyển động của động cơ, trục truyền động và chuyển động quay trong máy móc.",
  descEn: "Discovering motion principles, motor shafts, drive belts, and rotational energy mechanisms.",
  icon: Cog
}, {
  id: 4,
  code: "D4",
  badgeLabel: "STAGE 04",
  title: "Động cơ nhí",
  ageVi: "7 - 8 tuổi",
  ageEn: "Ages 7 - 8",
  descVi: "Tích hợp động cơ điện vào mô hình kỹ thuật, bước đầu kết nối lập trình kéo thả đơn giản.",
  descEn: "Integrating electric motors into engineering models, introducing basic drag-and-drop programming concepts.",
  icon: Cpu
}, {
  id: 5,
  code: "HP1",
  badgeLabel: "STAGE 05",
  title: "Kỹ sư cơ khí nhí",
  ageVi: "8 - 9 tuổi",
  ageEn: "Ages 8 - 9",
  descVi: "Nghiên cứu cấu trúc robot cơ bản, cảm biến lực và thuật toán điều khiển tuần tự nâng cao.",
  descEn: "Studying core robotics structures, force sensors, and sequential logic control algorithms.",
  icon: CircuitBoard
}, {
  id: 6,
  code: "HP2",
  badgeLabel: "STAGE 06",
  title: "Robot biết cảm nhận",
  ageVi: "9 - 10 tuổi",
  ageEn: "Ages 9 - 10",
  descVi: "Lập trình cảm biến khoảng cách, siêu âm, cảm biến màu sắc giúp robot phản hồi môi trường.",
  descEn: "Programming ultrasonic distance sensors and color detectors for real-time environment interaction.",
  icon: Bot
}, {
  id: 7,
  code: "HP3",
  badgeLabel: "STAGE 07",
  title: "Xe robot đầu tiên",
  ageVi: "10 - 11 tuổi",
  ageEn: "Ages 10 - 11",
  descVi: "Thiết kế hệ thống truyền động xe 2 cầu, lập trình di chuyển chính xác và tránh vật cản.",
  descEn: "Designing 2-wheel drive chassis, precision navigation algorithms, and obstacle avoidance systems.",
  icon: Rocket
}, {
  id: 8,
  code: "HP4",
  badgeLabel: "STAGE 08",
  title: "Xe robot thông minh",
  ageVi: "11 - 12 tuổi",
  ageEn: "Ages 11 - 12",
  descVi: "Tối ưu thuật toán dò đường PID, xử lý đa cảm biến và giải quyết bài toán mê cung.",
  descEn: "Optimizing line-following PID algorithms, multi-sensor telemetry, and complex maze solving.",
  icon: Cpu
}, {
  id: 9,
  code: "HP5",
  badgeLabel: "STAGE 09",
  title: "Thành phố xe tự lái",
  ageVi: "12 - 13 tuổi",
  ageEn: "Ages 12 - 13",
  descVi: "Mô phỏng hệ thống giao thông thông minh, kết nối truyền thông không dây v2x và nhận diện tín hiệu.",
  descEn: "Simulating smart traffic systems, wireless V2X communication, and automated signal recognition.",
  icon: Sparkles
}, {
  id: 10,
  code: "HP6",
  badgeLabel: "STAGE 10",
  title: "Robot nhiệm vụ",
  ageVi: "13 - 15 tuổi",
  ageEn: "Ages 13 - 15",
  descVi: "Thi công tay gắp khí nén, cơ cấu bắn bóng và hoàn thiện chiến thuật thi đấu sa bàn chuẩn quốc tế.",
  descEn: "Engineering pneumatic grippers, projectile mechanisms, and executing field strategies for international arenas.",
  icon: ShieldCheck
}, {
  id: 11,
  code: "AI-PREP",
  badgeLabel: "STAGE 11",
  title: "ENJOY AI PREPARATION",
  ageVi: "14 - 16 tuổi",
  ageEn: "Ages 14 - 16",
  descVi: "Hạ tầng luyện tập chuyên sâu cho các giải đấu Robotics & AI quy mô quốc gia và quốc tế.",
  descEn: "Advanced competition incubator for high-stakes national and global AI/Robotics tournaments.",
  icon: Sparkles,
  isSpecial: true
}];
export default function PathwayProgress() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  useEffect(() => {
    const saved = localStorage.getItem("roboto_lang");
    if (saved === "en" || saved === "vi") {
      setLang(saved);
    }
    const handleLangChange = (e: CustomEvent<{
      lang?: "vi" | "en";
    }> | Event) => {
      const detailLang = (e as CustomEvent)?.detail?.lang;
      if (detailLang === "vi" || detailLang === "en") {
        setLang(detailLang);
      } else {
        const current = localStorage.getItem("roboto_lang");
        if (current === "en" || current === "vi") {
          setLang(current);
        }
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    return () => window.removeEventListener("roboto-lang-change", handleLangChange);
  }, []);
  const isVi = lang === "vi";
  return <section id="roboticslearningpathwayprogress" data-nav="light" ref={sectionRef} className="relative py-20 lg:py-28 bg-background text-foreground overflow-hidden">
      {/* Blueprint Grid Texture Overlay */}
      <div className="absolute inset-0 pointer-events-none opacity-40 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:32px_32px]" aria-hidden="true" />

      {/* Decorative Blueprint Corner Accent */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" aria-hidden="true" />

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className={`max-w-3xl mx-auto text-center transition-all duration-700 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-muted text-primary font-mono text-xs font-semibold tracking-wider uppercase mb-4 border border-border">
            <CircuitBoard className="size-3.5 text-primary" />
            <span>[ SYSTEM_CURRICULUM // PATHWAY ]</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-foreground tracking-tight leading-tight">
            {isVi ? "NGƯỜI ĐỒNG HÀNH TRONG HÀNH TRÌNH ROBOTICS" : "Guiding Every Step of the Robotics Journey"}
          </h2>

          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default">
            {isVi ? "Lộ trình đào tạo chuẩn hóa 11 cấp độ từ mầm non đến chuyên sâu. Giảng viên Roboto luôn sát cánh giúp các em làm chủ tư duy phần cứng và thuật toán lập trình." : "A standardized 11-stage pathway from early foundation to advanced AI competition. Instructors guide students through hands-on hardware and programming logic."}
          </p>
        </div>

        {/* Instructor Guidance Callout Notice */}
        <div className={`mt-10 max-w-4xl mx-auto transition-all duration-700 delay-100 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="p-4 sm:p-5 rounded-xl bg-card border border-primary/20 shadow-xs flex flex-col sm:flex-row items-start gap-4">
            <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
              <Info className="size-5" />
            </div>
            <div className="text-sm text-foreground/90 leading-relaxed font-default space-y-1">
              <p className="font-semibold text-foreground">
                {isVi ? "Ghi chú chuyên môn giảng dạy:" : "Teaching Specialization Note:"}
              </p>
              <p className="text-muted-foreground">
                {isVi ? "Đội ngũ giảng viên dedicated đồng hành cùng học sinh qua các dự án phần cứng ngày càng phức tạp. Lưu ý: Mỗi giảng viên được phân công giảng dạy các cấp độ phù hợp với thế mạnh chuyên môn riêng, không phải mọi giáo viên đều đảm nhận toàn bộ 11 giai đoạn." : "Dedicated instructors guide students through increasingly complex concepts and hands-on hardware engineering projects across this progression. Please note that individual teachers specialize in specific stages, so not every teacher teaches every stage."}
              </p>
            </div>
          </div>
        </div>

        {/* Visual Course-Progression Diagram (D1 through ENJOY AI) */}
        <div className={`mt-10 max-w-5xl mx-auto transition-all duration-700 delay-150 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="relative rounded-2xl bg-[#0a0f1d] border border-slate-800 p-6 sm:p-8 shadow-2xl overflow-hidden text-white">
            {/* Tech blueprint grid pattern overlay */}
            <div className="absolute inset-0 pointer-events-none opacity-20 bg-[linear-gradient(to_right,#334155_1px,transparent_1px),linear-gradient(to_bottom,#334155_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
            
            {/* Glow accents */}
            <div className="absolute -top-24 -left-24 size-72 bg-blue-600/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 size-72 bg-secondary/15 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10">
              {/* Diagram Header */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-800">
                <div>
                  <div className="flex items-center gap-2 text-xs font-mono font-bold tracking-widest text-secondary uppercase">
                    <Layers className="size-4 text-secondary" />
                    <span>[ SYSTEM SCHEMATIC // COURSE PROGRESSION ]</span>
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mt-1">
                    {isVi ? "Sơ Đồ Tiến Trình Học Tập Robotics (D1  ENJOY AI)" : "Robotics Course Progression Map (D1  ENJOY AI)"}
                  </h3>
                </div>
                <div className="flex items-center gap-2 font-mono text-xs text-slate-400 bg-slate-900/80 px-3 py-1.5 rounded-lg border border-slate-800 self-start md:self-auto">
                  <span className="size-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span>11 {isVi ? "CẤP ĐỘ NỐI TIẾP" : "SEQUENTIAL STAGES"}</span>
                </div>
              </div>

              {/* Track Phase Indicators */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 my-6">
                <div className="bg-slate-900/90 border border-blue-500/30 rounded-xl p-3 flex items-center gap-3">
                  <div className="size-8 rounded-lg bg-blue-500/20 text-blue-400 flex items-center justify-center font-mono font-bold text-xs shrink-0">
                    01
                  </div>
                  <div>
                    <p className="text-xs font-mono text-blue-400 font-semibold uppercase">{isVi ? "Khối Mầm Non - Cơ Bản" : "Foundation Stage"}</p>
                    <p className="text-sm font-serif font-bold text-slate-100">D1  D4 <span className="text-xs font-sans font-normal text-slate-400">(4–8 {isVi ? "tuổi" : "yrs"})</span></p>
                  </div>
                </div>

                <div className="bg-slate-900/90 border border-indigo-500/30 rounded-xl p-3 flex items-center gap-3">
                  <div className="size-8 rounded-lg bg-indigo-500/20 text-indigo-400 flex items-center justify-center font-mono font-bold text-xs shrink-0">
                    02
                  </div>
                  <div>
                    <p className="text-xs font-mono text-indigo-400 font-semibold uppercase">{isVi ? "Khối Robotics Nâng Cao" : "Advanced Robotics"}</p>
                    <p className="text-sm font-serif font-bold text-slate-100">HP1  HP6 <span className="text-xs font-sans font-normal text-slate-400">(8–15 {isVi ? "tuổi" : "yrs"})</span></p>
                  </div>
                </div>

                <div className="bg-slate-900/90 border border-amber-500/30 rounded-xl p-3 flex items-center gap-3">
                  <div className="size-8 rounded-lg bg-amber-500/20 text-amber-400 flex items-center justify-center font-mono font-bold text-xs shrink-0">
                    03
                  </div>
                  <div>
                    <p className="text-xs font-mono text-amber-400 font-semibold uppercase">{isVi ? "Đấu Trường AI Quốc Tế" : "Competition Arena"}</p>
                    <p className="text-sm font-serif font-bold text-slate-100">ENJOY AI <span className="text-xs font-sans font-normal text-slate-400">(14–16 {isVi ? "tuổi" : "yrs"})</span></p>
                  </div>
                </div>
              </div>

              {/* Visual Progression Nodes Flow */}
              <div className="overflow-x-auto pb-4 pt-2 -mx-2 px-2 scrollbar-thin scrollbar-thumb-slate-700">
                <div className="min-w-[860px] flex items-center justify-between gap-1 relative py-4">
                  {/* Main Horizontal Flow Axis Line */}
                  <div className="absolute top-1/2 left-4 right-4 h-1 bg-slate-800 -translate-y-1/2 z-0" />
                  <div className="absolute top-1/2 left-4 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-amber-500 -translate-y-1/2 z-0 opacity-60" />

                  {STAGES.map((stg, i) => {
                  const isEnjoyAI = stg.code === "AI-PREP";
                  const isHP = stg.code.startsWith("HP");
                  return <div key={stg.id} data-index={i} className="relative z-10 flex flex-col items-center group">
                        {/* Stage Badge Node */}
                        <div className={`size-12 sm:size-14 rounded-xl flex flex-col items-center justify-center transition-all duration-300 shadow-lg border ${isEnjoyAI ? "bg-gradient-to-br from-amber-500 to-primary text-white border-amber-300 ring-4 ring-amber-500/20 scale-105" : isHP ? "bg-indigo-950 border-indigo-500/50 text-indigo-200 group-hover:border-indigo-400 group-hover:bg-indigo-900 group-hover:scale-105" : "bg-slate-900 border-blue-500/50 text-blue-200 group-hover:border-blue-400 group-hover:bg-slate-800 group-hover:scale-105"}`}>
                          <span className="font-mono text-xs font-black tracking-tighter leading-none">
                            {stg.code === "AI-PREP" ? "AI" : stg.code}
                          </span>
                          <span className="text-[9px] font-mono text-slate-400 mt-0.5 leading-none">
                            S{stg.id < 10 ? `0${stg.id}` : stg.id}
                          </span>
                        </div>

                        {/* Node Label Below */}
                        <div className="mt-2 text-center max-w-[76px]">
                          <p className="text-[11px] font-mono font-semibold text-slate-200 truncate">
                            {stg.code === "AI-PREP" ? "ENJOY AI" : stg.title.split(" ")[0]}
                          </p>
                          <p className="text-[10px] text-slate-400 font-mono">
                            {isVi ? stg.ageVi.replace(" tuổi", "t") : stg.ageEn.replace("Ages ", "")}
                          </p>
                        </div>

                        {/* Connector Arrow for non-last items */}
                        {i < STAGES.length - 1 && <ChevronRight className="absolute -right-2.5 top-5 size-4 text-slate-600 pointer-events-none" />}
                      </div>;
                })}
                </div>
              </div>

              {/* Pathway Summary Footnote */}
              <div className="mt-4 pt-4 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2 font-mono">
                <span className="flex items-center gap-1.5">
                  <Zap className="size-3.5 text-secondary" />
                  {isVi ? "Lộ trình học tích hợp phần cứng LEGO, lập trình thuật toán & thi đấu thực chiến." : "Integrated progression: LEGO mechanics, algorithmic logic & tournament preparation."}
                </span>
                <span className="text-secondary font-semibold">
                  D1  D2  D3  D4  HP1  HP2  HP3  HP4  HP5  HP6  ENJOY AI
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Pathway Stage Grid */}
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {STAGES.map((stage, idx) => {
          const IconComp = stage.icon;
          const delayMs = idx % 6 * 60;
          return <Card key={stage.id} data-index={idx} className={`relative bg-card border transition-all duration-500 hover:shadow-md hover:border-primary/40 group overflow-hidden ${stage.isSpecial ? "border-primary/50 ring-1 ring-primary/20 bg-primary/[0.02]" : "border-border"}`} style={{
            transitionDelay: `${delayMs}ms`,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? "translateY(0)" : "translateY(16px)"
          }}>
                {/* Blueprint watermark number */}
                <span className="absolute top-2 right-3 font-mono text-4xl font-bold text-muted-foreground/15 select-none pointer-events-none group-hover:text-primary/15 transition-colors">
                  {stage.id < 10 ? `0${stage.id}` : stage.id}
                </span>

                <CardContent className="p-6 relative z-10 flex flex-col h-full justify-between">
                  <div>
                    {/* Top Row: Technical Code & Badge */}
                    <div className="flex items-center justify-between gap-2 mb-4">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs font-bold px-2.5 py-1 rounded-md bg-primary text-primary-foreground tracking-wider uppercase">
                          {stage.code}
                        </span>
                        <Badge variant="outline" className="font-mono text-[11px] text-muted-foreground border-border bg-background">
                          {stage.badgeLabel}
                        </Badge>
                      </div>

                      <span className="font-mono text-xs text-muted-foreground font-medium">
                        {isVi ? stage.ageVi : stage.ageEn}
                      </span>
                    </div>

                    {/* Stage Title */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-muted text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors shrink-0">
                        <IconComp className="size-5" />
                      </div>
                      <h3 className="font-serif text-lg font-bold text-foreground leading-snug group-hover:text-primary transition-colors">
                        {stage.title}
                      </h3>
                    </div>

                    {/* Stage Description */}
                    <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed font-default">
                      {isVi ? stage.descVi : stage.descEn}
                    </p>
                  </div>

                  {/* Bottom Technical Status Indicator */}
                  <div className="mt-6 pt-3 border-t border-border/60 flex items-center justify-between text-xs text-muted-foreground font-mono">
                    <span className="inline-flex items-center gap-1.5 text-[11px]">
                      <CheckCircle2 className="size-3.5 text-secondary" />
                      {stage.isSpecial ? isVi ? "Giai đoạn thi đấu" : "Tournament Phase" : isVi ? "Chương trình chuẩn" : "Core Curriculum"}
                    </span>

                    {idx < STAGES.length - 1 && <span className="inline-flex items-center gap-1 text-[11px] text-primary/70 font-semibold group-hover:translate-x-0.5 transition-transform">
                        {isVi ? "Tiếp theo" : "Next"}
                        <ArrowRight className="size-3" />
                      </span>}
                  </div>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* CTA Footer Action */}
        <div className={`mt-16 text-center transition-all duration-700 delay-300 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 p-6 rounded-2xl bg-card border border-border shadow-xs max-w-2xl mx-auto">
            <div className="text-left flex-1">
              <h4 className="font-serif font-bold text-foreground text-lg">
                {isVi ? "Tìm hiểu chi tiết khung chương trình" : "Explore Full Curriculum Specs"}
              </h4>
              <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                {isVi ? "Xem đầy đủ mục tiêu đầu ra, công nghệ trang bị và dự án cho từng cấp học." : "View complete learning outcomes, technology kits, and projects for every level."}
              </p>
            </div>

            <Button asChild size="lg" className="font-semibold shrink-0 group">
              <Link to="/RoboticsLearningPathwayPage">
                <span>{isVi ? "XEM LỘ TRÌNH ROBOTICS" : "View the Robotics Pathway"}</span>
                <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}